import axios from 'axios';
import type { AxiosInstance, AxiosResponse } from 'axios';
import { ENV_CONFIG } from '../config/env';
import { logger } from '../utils/logger';

export interface ApiResponse<T = any> {
  success: boolean;
  data?: T;
  error?: string;
  message?: string;
}

/**
 * Location entity from the database
 */
export interface Location {
  id_location: number;
  id_endereco: number;
  id_nome: number;
  id_notificador: number;
  id_modelo?: number;
  id_status?: number;
  notificador?: string;
  notificado: string;
  protocolo?: string;
  endereco: string;
  modelo?: string;
  endereco_coordenada?: string;
  dispositivo_coordenada?: string;
  dentrodoraio?: boolean;
  data?: string;
  hora?: string;
}

/**
 * Complete diligence data with location information
 */
export interface DiligenceComplete extends Location {
  id_deligencia: number;
  documento: string;
  senha: string;
  endereco_coordenada: string;
  dispositivo_coordenada: string;
  dentrodoraio: boolean;
  data: string;
  hora: string;
}

/**
 * Diligence entity (note: API uses "deligencia" spelling)
 */
export interface Diligence {
  id_deligencia: number;
  id_location: number;
  id_modelo: number;
  endereco_coordenada: string | null;
  dispositivo_coordenada: string | null;
  dentrodoraio: boolean | null;
  data: string | null;
  hora: string | null;
}

export interface Notificator {
  id_notificador: number;
  notificador: string;
  documento: string;
  senha: string;
}

export interface Modelo {
  id_modelo: number;
  id_status: number;
  modelo: string;
}

export interface AuthenticatedUser {
  id_location?: number;
  id_notificador?: number;
  notificador: string;
  documento: string;
}

/**
 * API service for managing HTTP requests to the backend
 */
export class ApiService {
  private api: AxiosInstance;

  constructor() {
    this.api = axios.create({
      baseURL: ENV_CONFIG.API_BASE_URL,
      timeout: 10000,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (ENV_CONFIG.DEBUG_MODE) {
      logger.log('🌐 ApiService inicializado com baseURL:', ENV_CONFIG.API_BASE_URL || 'URL relativa (proxy)');
    }

    this.setupInterceptors();
  }

  private setupInterceptors(): void {
    if (ENV_CONFIG.DEBUG_MODE) {
      this.api.interceptors.request.use(
        (config) => {
          logger.log('API Request:', {
            url: config.url,
            method: config.method,
            data: config.data,
          });
          return config;
        }
      );

      this.api.interceptors.response.use(
        (response) => {
          if (ENV_CONFIG.DEBUG_MODE) {
            logger.log('API Response:', {
              url: response.config.url,
              status: response.status,
              data: response.data,
            });
          }
          return response;
        },
        (error) => {
          console.error('API Response Error:', {
            url: error.config?.url,
            status: error.response?.status,
            data: error.response?.data,
            message: error.message
          });
          return Promise.reject(error);
        }
      );
    }
  }

  private async request<T>(
    method: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH',
    endpoint: string,
    data?: any
  ): Promise<ApiResponse<T>> {
    try {
      const response: AxiosResponse<ApiResponse<T>> = await this.api.request({
        method,
        url: endpoint,
        data,
      });

      return response.data;
    } catch (error: any) {
      logger.error(`API ${method} Error:`, error);

      return {
        success: false,
        error: error.response?.data?.error || error.message || 'Erro de conexão com a API',
      };
    }
  }

  async testConnection(): Promise<ApiResponse> {
    return this.request('GET', '/api/health');
  }

  /**
   * Get locations with optional filtering by notificator
   */
  async getLocations(id_notificador?: number): Promise<ApiResponse<Location[]>> {
    let endpoint = '/api/location';
    const params = new URLSearchParams();

    if (id_notificador) {
      params.append('id_notificador', id_notificador.toString());
    }
    params.append('site', 'true');

    if (params.toString()) {
      endpoint += `?${params.toString()}`;
    }

    const response = await this.request<Location[]>('GET', endpoint); 
    
    if (response.success && response.data) {
      return {
        success: true,
        data: response.data
      };
    }

    return {
      success: false,
      error: response.error || 'Erro ao buscar localizações'
    };
  }

  async getLocationById(id: number): Promise<ApiResponse<Location>> {
    return this.request<Location>('GET', `/api/location/${id}`);
  }

  /**
   * Get location by ID with optional related data inclusion
   */
  async getLocationByIdWithFilters(id: number, includeDetails: boolean = true): Promise<ApiResponse<Location>> {
    let endpoint = `/api/location/${id}`;

    if (includeDetails) {
      endpoint += '?site=true';
    }

    return this.request<Location>('GET', endpoint);
  }

  async createLocation(location: Omit<Location, 'id_location'>): Promise<ApiResponse<Location>> {
    return this.request<Location>('POST', '/api/location', location);
  }

  async updateLocation(id: number, location: Partial<Location>): Promise<ApiResponse<Location>> {
    return this.request<Location>('PATCH', `/api/location/${id}`, location);
  }

  async deleteLocation(id: number): Promise<ApiResponse> {
    return this.request('DELETE', `/api/location/${id}`);
  }

  /**
   * Authenticate user with document and password
   */
  async authenticateUser(login: string, senha: string): Promise<ApiResponse<AuthenticatedUser>> {
    try {
      const normalizedDocumento = login.toUpperCase();

      const authResponse = await this.request<AuthenticatedUser>('GET', `/api/users/auth-new?documento=${encodeURIComponent(normalizedDocumento)}&senha=${encodeURIComponent(senha)}`);

      if (authResponse.success && authResponse.data) {
        const user: AuthenticatedUser = {
          id_notificador: authResponse.data.id_notificador,
          notificador: authResponse.data.notificador,
          documento: normalizedDocumento
        };

        return {
          success: true,
          data: user
        };
      } else {
        if (ENV_CONFIG.IS_PRODUCTION) {
          return {
            success: false,
            error: authResponse.error || 'Documento ou senha incorretos.'
          };
        }

        // Development fallback with hardcoded credentials
        const devCredentials = [
          { documento: 'ADMIN', senha: '123456', notificador: 'Administrador' },
          { documento: 'TEST', senha: 'test123', notificador: 'Usuário Teste' },
          { documento: 'USER', senha: 'user123', notificador: 'Usuário Demo' }
        ];

        const devUser = devCredentials.find(cred =>
          cred.documento === normalizedDocumento && cred.senha === senha
        );

        if (devUser) {
          const id_notificador = Math.abs(normalizedDocumento.split('').reduce((a, b) => {
            a = ((a << 5) - a) + b.charCodeAt(0);
            return a & a;
          }, 0));

          const user: AuthenticatedUser = {
            id_notificador,
            notificador: devUser.notificador,
            documento: normalizedDocumento
          };

          return {
            success: true,
            data: user
          };
        }
      }

      return {
        success: false,
        error: 'Documento ou senha incorretos.'
      };

    } catch (error: any) {
      return {
        success: false,
        error: 'Erro de conexão com o servidor. Tente novamente.'
      };
    }
  }

  /**
   * Get all notificators with pagination data
   */
  async getNotificators(): Promise<ApiResponse<Notificator[]>> {
    const response = await this.request<{ notificators: Notificator[], pagination: any }>('GET', '/api/notificators');

    if (response.success && response.data?.notificators) {
      return {
        success: true,
        data: response.data.notificators
      };
    }

    return {
      success: false,
      error: response.error || 'Erro ao buscar notificadores'
    };
  }

  async getNotificatorById(id: number): Promise<ApiResponse<Notificator>> {
    return this.request<Notificator>('GET', `/api/notificators/${id}`);
  }

  /**
   * Get all diligencias (note: API endpoint uses "deligence" spelling)
   */
  async getDiligencias(): Promise<ApiResponse<DiligenceComplete[]>> {
    const response = await this.request<DiligenceComplete[]>('GET', '/api/deligence');

    if (response.success && response.data) {
      return {
        success: true,
        data: response.data
      };
    }

    return {
      success: false,
      error: response.error || 'Erro ao buscar diligências'
    };
  }

  async createDiligencia(diligencia: Omit<Diligence, 'id_deligencia'>): Promise<ApiResponse<Diligence>> {
    return this.request<Diligence>('POST', '/api/deligence', diligencia);
  }

  async getDiligenciaById(id: number): Promise<ApiResponse<Diligence>> {
    return this.request<Diligence>('GET', `/api/deligence/${id}`);
  }

  async updateDiligencia(id: number, diligencia: Partial<Diligence>): Promise<ApiResponse<Diligence>> {
    return this.request<Diligence>('PUT', `/api/deligence/${id}`, diligencia);
  }

  async deleteDiligencia(id: number): Promise<ApiResponse> {
    return this.request('DELETE', `/api/deligence/${id}`);
  }

  /**
   * Get modelos with optional status filtering
   */
  async getModelos(id_status?: number): Promise<ApiResponse<Modelo[]>> {
    try {
      let url = 'https://notificacao-api.vercel.app/api/modelos';
      if (id_status !== undefined) {
        url += `?id_status=${id_status}`;
      }
      const response = await axios.get(url);
      
      if (response.data && Array.isArray(response.data.data)) {
        return { success: true, data: response.data.data };
      }
      
      if (Array.isArray(response.data)) {
        return { success: true, data: response.data };
      }
      
      if (response.data && Array.isArray(response.data.modelos)) {
        return { success: true, data: response.data.modelos };
      }
      
      return { success: false, error: 'Formato inesperado na resposta da API de modelos.' };
    } catch (error: any) {
      logger.error('Erro ao buscar modelos:', error);
      return { success: false, error: error.message || 'Erro ao buscar modelos' };
    }
  }

  async getModeloById(id: number): Promise<ApiResponse<Modelo>> {
    return this.request<Modelo>('GET', `/api/modelos/${id}`);
  }

  async createModelo(modelo: Omit<Modelo, 'id_modelo'>): Promise<ApiResponse<Modelo>> {
    return this.request<Modelo>('POST', '/api/modelos', modelo);
  }

  async updateModelo(id: number, modelo: Partial<Modelo>): Promise<ApiResponse<Modelo>> {
    return this.request<Modelo>('PUT', `/api/modelos/${id}`, modelo);
  }

  async deleteModelo(id: number): Promise<ApiResponse> {
    return this.request('DELETE', `/api/modelos/${id}`);
  }

}

// Singleton instance
export const apiService = new ApiService();
export default apiService;
